import { atom, onConnect, readonly, withAssign } from '@reatom/framework'

type FpsPerformance = 'poor' | 'good' | undefined

function createRingBuffer(
  size: number,
  values?: Array<number | undefined>,
  index?: number,
) {
  return {
    size,
    values:
      values ??
      Array.from<number | undefined>({ length: size }).fill(undefined),
    index: index ?? 0,

    push(value: number) {
      const newValues = [...this.values]
      newValues[this.index] = value

      return createRingBuffer(
        this.size,
        newValues,
        (this.index + 1) % this.size,
      )
    },

    last() {
      return this.values[(this.index - 1 + this.size) % this.size]
    },

    reset() {
      return createRingBuffer(this.size)
    },
  }
}
interface RingBuffer extends ReturnType<typeof createRingBuffer> {}

const reatomFPS = (options: {
  windowWidth: number
  slowThreshold: number
  samplesCount: number
}) => {
  const fpsListAtom = atom<RingBuffer>(
    createRingBuffer(options.windowWidth),
    'fpsListAtom',
  )

  const maxAtom = atom(
    (ctx) =>
      Math.max(...ctx.spy(fpsListAtom).values.filter((x) => x !== undefined)),
    'maxAtom',
  )
  const currentAtom = atom((ctx) => ctx.spy(fpsListAtom).last(), 'currentAtom')

  const avgAtom = atom((ctx) => {
    const fpsList = ctx.spy(fpsListAtom).values.filter((x) => x !== undefined)
    const sum = fpsList.reduce((a, b) => a + b, 0)

    return fpsList.length > 0 ? Math.round(sum / fpsList.length) : undefined
  }, 'avgAtom')

  const performanceAtom = atom<FpsPerformance>((ctx) => {
    const fpsList = ctx.spy(fpsListAtom)
    const avgFps = ctx.spy(avgAtom)

    if (fpsList.values.length <= options.samplesCount || avgFps === undefined) {
      return undefined
    }

    return avgFps > options.slowThreshold ? 'good' : 'poor'
  }, 'performanceAtom')

  onConnect(fpsListAtom, (ctx) => {
    let frames = 0
    let lastTime = performance.now()
    let animId = 0

    function loop() {
      const t = performance.now()
      frames++

      if (t >= lastTime + 1000) {
        const elapsed = t - lastTime
        const fps = Math.round((frames * 1000) / elapsed)

        fpsListAtom(ctx, (x) => x.push(fps))

        // reset counters
        frames = 0
        lastTime = t
      }

      animId = requestAnimationFrame(loop)
    }

    animId = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(animId)
      fpsListAtom(ctx, (x) => x.reset())
    }
  })

  return fpsListAtom.pipe(
    readonly,
    withAssign(() => ({
      maxAtom,
      avgAtom,
      performanceAtom,
      currentAtom,
    })),
  )
}
// eslint-disable-next-line @reatom/unit-naming-rule
export const FPSAtom = reatomFPS({
  windowWidth: 100,
  samplesCount: 3,
  slowThreshold: 40,
})
